import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import useCertifierSelectDialog from 'dialogs/CompanyDetailsDialog/hooks/useCertifierSelectDialog';

import EntitySelectDialog from '../../components/EntitySelectDialog/EntitySelectDialog';
import { ReduxState } from '../../redux/types';
import User from '../../types/User';
import api from '../../utils/api';
import { useDialog } from '../../utils/dialog';
import { getGravatar } from '../../utils/gravatar';
import snackbar from '../../utils/snackbar';
import useAuditorSelectDialog from '../CompanyDetailsDialog/hooks/useAuditorSelectDialog';
import useUserSelectDialog from '../CompanyDetailsDialog/hooks/useUserSelectDialog';

/*
 * Show the list of users/auditors of a company.
 */

interface Props {
    open: boolean;
    onClose: (selectedId?: number) => void;
    companyId: number;
    users: User[];
    type: 'customer' | 'auditor' | 'certifier';
    updateData: () => void;
    isLoading?: boolean;
}

const CompanyUsersDialog = (props: Props) => {
    const { open, onClose, companyId, users, type, updateData, isLoading } = props;

    const { t } = useTranslation();
    const isAdmin = useSelector((state: ReduxState) => state.user.type) === 'admin';
    const isCertifier = useSelector((state: ReduxState) => state.user.type) === 'certifier';

    const userIds = users.map(user => user.id);

    const [certifierSelectDialog, openCertifierSelectDialog] = useCertifierSelectDialog(companyId, userIds, updateData);
    const [auditorSelectDialog, openAuditorSelectDialog] = useAuditorSelectDialog(companyId, userIds, updateData);
    const [userSelectDialog, openUserSelectDialog] = useUserSelectDialog(companyId, userIds, updateData);
    const [localIsLoading, setLocalIsLoading] = useState(false);

    const [openDialog, closeDialog] = useDialog();

    const openUserAuditorSelect = useCallback(() => {
        // open the correct dialog, based on type passed in props
        if (type === 'auditor') {
            openAuditorSelectDialog();
        }
        else if (type === 'customer') {
            openUserSelectDialog();
        } else {
            openCertifierSelectDialog();
        }
    }, [type, openAuditorSelectDialog, openUserSelectDialog, openCertifierSelectDialog]);

    const deleteUserAuditor = useCallback((userId: number) => {
        // function called on user/auditor delete request
        setLocalIsLoading(true);
        api.request('/companies/' + companyId + '/' + (type === 'customer' ? 'users' : (type === 'auditor' ? 'auditors' : 'certifiers')) + '/' + userId, 'DELETE').then(() => {
            const messages = {
                customer: t('dialogs.company_users.user_removed'),
                auditor: t('dialogs.company_users.auditor_removed'),
                certifier: t('dialogs.company_users.certifier_removed')
            };
            snackbar.success(messages[type]);
            updateData();
        }).finally(() => {
            setLocalIsLoading(false);
        });
    }, [companyId, t, type, updateData]);

    const deleteUserAuditorConfirm = useCallback((userId: number) => {
        const dialogTitles = {
            customer: t('dialogs.company_users.user_remove_confirm_title'),
            auditor: t('dialogs.company_users.auditor_remove_confirm_title'),
            certifier: t('dialogs.company_users.certifier_remove_confirm_title'),
        };

        const messages = {
            customer: t('dialogs.company_users.user_remove_confirm'),
            auditor: t('dialogs.company_users.auditor_remove_confirm'),
            certifier: t('dialogs.company_users.certifier_remove_confirm')
        };

        openDialog({
            title: dialogTitles[type],
            message: messages[type],
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteUserAuditor(userId);
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteUserAuditor, openDialog, t, type]);

    const dataForRender = useMemo(() => {
        // locations list is arranged to be used by EntitySelectDialog component
        return users.map(user => {
            return {
                id: user.id,
                name: user.surname + ' ' + user.name,
                description: user.email,
                canDelete: user.canDelete,
                image: getGravatar(user.email)
            };
        });
    }, [users]);

    const titles = {
        customer: t('dialogs.company_users.users'),
        auditor: t('dialogs.company_users.auditors'),
        certifier: t('dialogs.company_users.certifiers')
    };

    const newEntityBtnTexts = {
        customer: t('dialogs.company_users.add_user'),
        auditor: t('dialogs.company_users.add_auditor'),
        certifier: t('dialogs.company_users.add_certifier')
    };

    const notFoundMessages = {
        customer: t('dialogs.user_select.no_customer_found'),
        auditor: t('dialogs.user_select.no_auditor_found'),
        certifier: t('dialogs.user_select.no_certifier_found'),
    };

    const notFoundMessage = notFoundMessages[type ?? 'customer'];

    const title = titles[type];
    const newEntityBtnText = newEntityBtnTexts[type];

    return (
        <>
            <EntitySelectDialog
                open={open}
                title={title}
                onClose={onClose}
                data={(isLoading || localIsLoading) ? [] : dataForRender}
                onDelete={(isAdmin || (isCertifier && type !== 'certifier')) ? deleteUserAuditorConfirm : undefined}
                newEntityBtnPosition='list'
                onNewEntityClick={(isAdmin || (isCertifier && type !== 'certifier')) ? openUserAuditorSelect : undefined}
                newEntityBtnText={newEntityBtnText}
                disableItemsClick
                noEntityFoundMessage={notFoundMessage}
                isLoading={isLoading || localIsLoading}
            />

            {/* users select dialogs are injected */}
            {auditorSelectDialog}
            {userSelectDialog}
            {certifierSelectDialog}
        </>
    );
};

export default CompanyUsersDialog;
